import { useState } from "react";
import { Plus } from "lucide-react";
import { useChartOfAccounts, useDeleteChartOfAccount, ACCOUNT_TYPE_LABEL, AccountType } from "@/api/accounting";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "@/api/client";
import CreateChartOfAccountModal from "./CreateChartOfAccountModal";

const TYPE_STYLE: Record<AccountType, string> = {
  ACTIVO: "bg-blue-100 text-blue-700",
  PASIVO: "bg-red-100 text-red-700",
  PATRIMONIO: "bg-purple-100 text-purple-700",
  INGRESO: "bg-green-100 text-green-700",
  GASTO: "bg-amber-100 text-amber-700",
  COSTO: "bg-orange-100 text-orange-700",
};

const TYPE_OPTIONS = Object.keys(ACCOUNT_TYPE_LABEL) as AccountType[];

export default function ChartOfAccountsPage() {
  const [accountType, setAccountType] = useState<AccountType | "">("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useChartOfAccounts({
    accountType: accountType || undefined,
    search: search || undefined,
    pageSize: 200,
  });
  const deleteAccount = useDeleteChartOfAccount();

  const accounts = data?.data ?? [];

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`¿Eliminar la cuenta "${name}"?`)) return;
    try {
      await deleteAccount.mutateAsync(id);
      toast.success("Cuenta eliminada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Plan de cuentas</h1>
          <p className="text-sm text-gray-500">Catálogo contable de la organización, usado en los asientos de partida doble.</p>
        </div>
        {hasPermission("accounting.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nueva cuenta
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por código o nombre..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-field w-auto" value={accountType} onChange={(e) => setAccountType(e.target.value as AccountType | "")}>
          <option value="">Todos los tipos</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {ACCOUNT_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Código</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Tipo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cuenta padre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              {hasPermission("accounting.delete") && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && accounts.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  No hay cuentas contables que coincidan con los filtros.
                </td>
              </tr>
            )}
            {accounts.map((account) => (
              <tr key={account.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-700">{account.code}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{account.name}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_STYLE[account.accountType]}`}>
                    {ACCOUNT_TYPE_LABEL[account.accountType]}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{account.parent ? `${account.parent.code} — ${account.parent.name}` : "—"}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      account.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {account.isActive ? "Activa" : "Inactiva"}
                  </span>
                </td>
                {hasPermission("accounting.delete") && (
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      className="text-xs font-medium text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(account.id, account.name)}
                    >
                      Eliminar
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateChartOfAccountModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
